import styled, { css, FlattenSimpleInterpolation } from 'styled-components';

import { TTheme, TThemeColours, WithTheme } from '@pb/styles';
import { getValueFromMap } from '@pb/helpers';

import { TButtonAppearance, TStyledButtonProperties } from './button.types';

const baseColorMap = new Map<TButtonAppearance, TThemeColours>([
    ['default', 'purple'],
    ['alternative', 'darkPurple'],
    ['danger', 'red'],
    ['marketing', 'indigo'],
]);

const hoverColorMap = new Map<TButtonAppearance, TThemeColours>([
    ['default', 'purpleTint10'],
    ['alternative', 'darkPurpleTint10'],
    ['danger', 'redTint10'],
    ['marketing', 'indigoTint10'],
]);

const getThemeColorFromMap =
    (map: Map<TButtonAppearance, TThemeColours>) =>
    (theme: TTheme, color: TButtonAppearance): string => {
        const colorName = getValueFromMap(map, color);

        return theme.color[colorName];
    };

const getBaseColor = getThemeColorFromMap(baseColorMap);
const getHoverColor = getThemeColorFromMap(hoverColorMap);

export const getWidthStyles = ({
    level,
    width,
}: WithTheme<TStyledButtonProperties>): FlattenSimpleInterpolation => {
    if (width === 'min') {
        return css`
            width: auto;
        `;
    }

    if (width === 'max') {
        return css`
            width: 100%;
        `;
    }

    if (level === 'tertiary') {
        return css`
            width: auto;
        `;
    }

    return css`
        width: auto;
        min-width: 13.6rem;
    `;
};

export const getBaseAppearanceStyles = ({
    appearance,
    showOnDarkBackground,
    level,
    theme,
}: WithTheme<TStyledButtonProperties>): FlattenSimpleInterpolation => {
    const themeColor = getBaseColor(theme, appearance);
    const additionalColor = showOnDarkBackground
        ? theme.color.white
        : themeColor;

    if (level === 'secondary') {
        return css`
            color: ${themeColor};
            background-color: ${theme.color.white};
            border-color: ${additionalColor};
        `;
    }

    if (level === 'tertiary') {
        return css`
            padding: 1.2rem 0;
            color: ${additionalColor};
            border-color: transparent;
        `;
    }

    return css`
        color: ${theme.color.white};
        background-color: ${themeColor};
        border-color: ${additionalColor};
    `;
};

export const getHoverStyles = ({
    appearance,
    level,
    showOnDarkBackground,
    theme,
}: WithTheme<TStyledButtonProperties>): FlattenSimpleInterpolation => {
    const themeColor = getHoverColor(theme, appearance);

    if (level === 'secondary') {
        return css`
            background-color: ${theme.color.grey2};
            border-color: ${showOnDarkBackground ? theme.color.grey2 : null};
        `;
    }

    if (level === 'tertiary') {
        return css`
            text-decoration: underline;
        `;
    }

    if (showOnDarkBackground) {
        return css`
            background-color: ${themeColor};
        `;
    }

    return css`
        background-color: ${themeColor};
        border-color: ${themeColor};
    `;
};

export const getFocusStyles = ({
    appearance,
    theme,
}: WithTheme<TStyledButtonProperties>): FlattenSimpleInterpolation => {
    const focusColor =
        appearance === 'danger'
            ? getHoverColor(theme, 'danger')
            : theme.color.purpleTint20;
    const innerShadow = `0 0 0 0.2rem ${theme.color.white}`;
    const outerShadow = `0 0 0 0.4rem ${focusColor}`;
    const boxShadow = `${innerShadow}, ${outerShadow}`;

    return css`
        box-shadow: ${boxShadow};

        &:not(:focus-visible) {
            box-shadow: none;
        }
    `;
};

export const getDisabledStyles = ({
    level,
    showOnDarkBackground,
    theme,
}: WithTheme<TStyledButtonProperties>): FlattenSimpleInterpolation => {
    if (level === 'secondary') {
        const borderColor = showOnDarkBackground
            ? theme.color.grey5
            : undefined;
        return css`
            color: ${theme.color.grey5};
            background-color: ${theme.color.grey3};
            border-color: ${borderColor};
        `;
    }

    if (level === 'tertiary') {
        return css`
            color: ${theme.color.grey5};
        `;
    }

    const borderColor = showOnDarkBackground ? theme.color.grey3 : undefined;
    return css`
        color: ${theme.color.grey2};
        background-color: ${theme.color.grey5};
        border-color: ${borderColor};
    `;
};

export const StyledButton = styled.button<TStyledButtonProperties>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 4.4rem;
    margin: 0;
    padding: 0 1.2rem;
    font-weight: ${({ theme }) => theme.font.weight.bold};
    font-size: ${({ theme }) => theme.font.size.base};
    font-family: ${({ theme }) => theme.font.family.secondary};
    line-height: 2rem;
    white-space: nowrap;
    text-align: center;
    text-decoration: none;
    background: transparent;
    border-style: solid;
    border-width: 0.1rem;
    border-radius: ${({ theme }) => theme.borderRadius.default};
    outline: none;
    cursor: pointer;

    ${getBaseAppearanceStyles}
    ${getWidthStyles}

    &:disabled {
        border: none;
        cursor: not-allowed;
        ${getDisabledStyles}
    }

    &:focus {
        ${getHoverStyles}
        ${getFocusStyles}
    }

    &:not(:disabled) {
        &:hover {
            ${getHoverStyles}
        }
    }
`;
